import { gql, useQuery } from '@apollo/client';
import {
  ListMetadataKeysRequest,
  ListUnifiedFieldsInput,
  Query,
} from 'generated/graphql';
import { useMemo } from 'react';
import { apolloStatusToQueryStatus } from 'v2/core/repository/edge-server/statusConvertor';
import { useGetUser } from 'v2/core/services/user';

export const RESOLVE_METADATA_KEY_GQL = gql`
  query resolveMetadataKeys(
    $tenantID: ID!
    $filter: ListMetadataKeysFilter
    $query: String
    $key: String
    $limitValues: Int
  ) {
    listMetadataKeys(request: { tenantID: $tenantID, filter: $filter }) {
      keys {
        key
        displayText
        type
        description
      }
    }

    listUnifiedFields(
      request: {
        tenantID: $tenantID
        query: $query
        key: $key
        limitValues: $limitValues
      }
    ) {
      fields {
        eqlField {
          detail {
            metadata {
              source
              references {
                displayName
                iconURL
                key
                source
              }
            }
          }
        }
        values {
          displayName
          iconURL
          value
        }
      }
    }
  }
`;
export const useResolveMetadataKey = (key: string) => {
  const {
    tenant: { id: tenantID },
  } = useGetUser();

  const { data, loading, refetch, networkStatus, called, error } = useQuery<
    Query,
    ListMetadataKeysRequest & ListUnifiedFieldsInput
  >(RESOLVE_METADATA_KEY_GQL, {
    variables: {
      tenantID,
      filter: {
        keys: [key],
      },
      key: key,
    },
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'no-cache',
  });

  const metadata = useMemo(
    () => ({
      ...data?.listMetadataKeys?.keys?.[0],
      values: data?.listUnifiedFields?.fields?.[0]?.values ?? [],
      references:
        data?.listUnifiedFields?.fields?.[0]?.eqlField?.detail?.metadata
          ?.references,
    }),
    [data],
  );

  const status = apolloStatusToQueryStatus(
    loading,
    called,
    false,
    error,
    networkStatus,
  );

  return {
    metadata: metadata,
    loading,
    refetch,
    status,
  };
};
